import { useCallback, useEffect, useRef, useState } from 'react'
import { Plus, Search, X } from 'lucide-react'
import type { OrderWorkflowTracker } from '@/lib/order-workflow-tracking'

import type { Customer } from '@/lib/api/customer-api'
import { Input } from '@/components/ui/input'
import {
    useCustomerGroups,
    useInfiniteCustomersQuery,
} from '@/hooks/use-customer-query'
import { CustomerForm } from '@/components/forms/customer-form'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { m } from '@/lib/paraglide/messages'

/** Simple debounce hook */
function useDebounce(value: string, delay: number) {
    const [debounced, setDebounced] = useState(value)
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay)
        return () => clearTimeout(timer)
    }, [value, delay])
    return debounced
}

interface Props {
    workflow?: OrderWorkflowTracker
    value?: Customer | null
    onChange: (customer: Customer | null) => void
    defaultCustomerId?: string
}

export function CustomerSearchSelect({
    value,
    onChange,
    defaultCustomerId,
    workflow,
}: Props) {
    const [search, setSearch] = useState('')
    const searchId = useRef<string | null>(null)
    const lastResult = useRef('')
    const searchKind = useRef<'browse' | 'search'>('browse')
    const [open, setOpen] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)
    const { data: groups = [] } = useCustomerGroups()
    const wrapperRef = useRef<HTMLDivElement>(null)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const sentinelRef = useRef<HTMLDivElement>(null)
    const debouncedSearch = useDebounce(search, 300)

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isFetching,
        isError,
        isPlaceholderData,
    } = useInfiniteCustomersQuery(debouncedSearch)

    const customers = data?.pages.flatMap((p) => p.items) ?? []
    const isEmpty =
        !isFetching && data !== undefined && data.pages[0]?.items.length === 0

    useEffect(() => {
        if (
            !workflow ||
            !open ||
            !searchId.current ||
            search !== debouncedSearch ||
            isFetching ||
            isError ||
            isPlaceholderData ||
            data === undefined
        )
            return
        const resultKey = `${searchId.current}:${customers.length}:${Boolean(hasNextPage)}`
        if (lastResult.current === resultKey) return
        lastResult.current = resultKey
        workflow.search({
            searchId: searchId.current,
            entityKind: 'customer',
            queryKind: searchKind.current,
            queryLength: debouncedSearch.length,
            resultCount: customers.length,
            hasMore: Boolean(hasNextPage),
        })
    }, [
        workflow,
        open,
        search,
        debouncedSearch,
        isFetching,
        isError,
        isPlaceholderData,
        data,
        customers.length,
        hasNextPage,
    ])

    useEffect(() => {
        workflow?.setContext({
            customerLookupState: value ? 'prefilled' : 'unselected',
        })
    }, [workflow])

    // Auto-select default customer on mount
    useEffect(() => {
        if (defaultCustomerId && !value && customers.length > 0) {
            const found = customers.find((c) => c.id === defaultCustomerId)
            if (found) {
                workflow?.setContext({ customerLookupState: 'prefilled' })
                onChange(found)
            }
        }
    }, [defaultCustomerId, customers, value, onChange])

    // IntersectionObserver: load next page when sentinel enters the scroll container viewport
    const handleIntersect = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            if (
                entries[0]?.isIntersecting &&
                hasNextPage &&
                !isFetchingNextPage
            ) {
                void fetchNextPage()
            }
        },
        [fetchNextPage, hasNextPage, isFetchingNextPage],
    )

    useEffect(() => {
        const sentinel = sentinelRef.current
        const root = scrollContainerRef.current
        if (!sentinel) return

        const observer = new IntersectionObserver(handleIntersect, {
            root,
            threshold: 0.1,
        })
        observer.observe(sentinel)
        return () => observer.disconnect()
        // customers.length triggers re-create after the dropdown's conditional
        // sentinel renders; without it the first page never loads page 2.
    }, [handleIntersect, open, customers.length])

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target as Node)
            ) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const handleSelect = (customer: Customer) => {
        workflow?.setContext({ customerLookupState: 'selected' })
        workflow?.step('customer_selected', {
            fieldGroup: 'customer',
            ...(!isFetching &&
            !isError &&
            !isPlaceholderData &&
            search === debouncedSearch
                ? {
                      searchId: searchId.current ?? undefined,
                      candidateRank:
                          customers.findIndex((c) => c.id === customer.id) + 1,
                  }
                : {}),
        })
        onChange(customer)
        setSearch('')
        setOpen(false)
    }

    const handleClear = () => {
        workflow?.setContext({ customerLookupState: 'unselected' })
        onChange(null)
        setSearch('')
    }

    if (value) {
        return (
            <div className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2 text-sm">
                <div className="flex-1 min-w-0">
                    <span className="font-medium">{value.name}</span>
                    <span className="text-muted-foreground ml-2">
                        {value.phone ?? ''}
                    </span>
                    {value.address && (
                        <span className="text-muted-foreground ml-2 text-xs">
                            — {value.address}
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={handleClear}
                    className="shrink-0 text-muted-foreground hover:text-foreground"
                >
                    <X className="size-4" />
                </button>
            </div>
        )
    }

    return (
        <>
            <div ref={wrapperRef} className="relative">
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
                        <Input
                            placeholder={m.customer_search_placeholder()}
                            className="pl-9"
                            value={search}
                            onChange={(e) => {
                                searchKind.current = 'search'
                                if (workflow)
                                    searchId.current = crypto.randomUUID()
                                workflow?.interact('customer')
                                setSearch(e.target.value)
                                setOpen(true)
                            }}
                            onFocus={() => {
                                if (workflow && !searchId.current)
                                    searchId.current = crypto.randomUUID()
                                setOpen(true)
                            }}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => setDialogOpen(true)}
                        className="shrink-0 rounded-md border bg-background px-2.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                        title={m.customer_add_new()}
                    >
                        <Plus className="size-4" />
                    </button>
                </div>
                {open && customers.length > 0 && (
                    <div
                        ref={scrollContainerRef}
                        className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg max-h-56 overflow-auto"
                    >
                        {customers.map((c) => (
                            <button
                                key={c.id}
                                type="button"
                                onClick={() => handleSelect(c)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-accent border-b last:border-0"
                            >
                                <div className="flex justify-between gap-2">
                                    <span className="font-medium truncate">
                                        {c.name}
                                    </span>
                                    <span className="text-muted-foreground shrink-0">
                                        {c.phone ?? '—'}
                                    </span>
                                </div>
                                {c.address && (
                                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                                        {c.address}
                                    </div>
                                )}
                            </button>
                        ))}
                        {isFetchingNextPage && (
                            <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                                {m.customer_loading_more()}
                            </div>
                        )}
                        <div ref={sentinelRef} className="h-px" />
                    </div>
                )}
                {open && isEmpty && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg px-3 py-2 text-sm text-muted-foreground">
                        {m.customer_not_found()}
                    </div>
                )}
            </div>

            {/* New customer dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{m.customer_add_new()}</DialogTitle>
                        <DialogDescription>
                            {m.customer_create_for_order()}
                        </DialogDescription>
                    </DialogHeader>
                    <CustomerForm
                        onCreated={(customer) => {
                            // API tạo khách chỉ trả customer_group_id, không kèm
                            // object customer_group → prefill CK nhóm ở form đơn
                            // hàng sẽ = 0. Gán lại nhóm từ danh sách đã có để
                            // khớp path chọn khách từ dropdown (vốn có nhóm).
                            const enriched =
                                customer.customer_group ||
                                !customer.customer_group_id
                                    ? customer
                                    : {
                                          ...customer,
                                          customer_group: groups.find(
                                              (g) =>
                                                  g.id ===
                                                  customer.customer_group_id,
                                          ),
                                      }
                            workflow?.setContext({
                                customerLookupState: 'selected',
                            })
                            onChange(enriched)
                            setDialogOpen(false)
                        }}
                        onSuccess={() => setDialogOpen(false)}
                        onCancel={() => setDialogOpen(false)}
                    />
                </DialogContent>
            </Dialog>
        </>
    )
}
